import { motion } from "framer-motion";
import { X, Check } from "@phosphor-icons/react";
import { SectionHeader } from "@/components/RevenueEngine";

const OLD = [
  { t: "Decaying Data", d: "Contacts go stale, duplicates pile up, and nobody has time to fix it." },
  { t: "Blind Outreach", d: "Guessing who to contact, blasting generic messages." },
  { t: "Manual Overhead", d: "Scaling pipeline means scaling payroll." },
  { t: "Missed Timing", d: "Ready-to-buy accounts fall silent because no one caught the signal." },
  { t: "Siloed Tools", d: "Marketing, Sales, and RevOps each working from different data, none of it matching." },
];

const NEW = [
  { t: "Dynamic Foundation", d: "A CRM that stays clean and a TAM that updates itself." },
  { t: "Signal-Led Targeting", d: "Only engage when accounts show active intent." },
  { t: "Zero Extra Headcount", d: "Research, enrichment, and routing happen automatically. No new hires needed." },
  { t: "Speed-to-Lead", d: "24/7 radar, instant routing before prospects go cold." },
  { t: "Unified RevOps", d: "Every signal, every workflow, every rep action running through one connected system." },
];

export default function Paradigm() {
  return (
    <section
      id="paradigm"
      data-testid="paradigm"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="The Worldview / 05"
          title={<>Your pipeline isn&apos;t a lead problem.<br /><span className="text-[#D946EF]">It&apos;s an infrastructure problem.</span></>}
          sub="Most teams try to scale revenue by adding headcount, buying more lists, and sending more emails. It doesn't compound. It just costs more."
        />

        {/* Visualized old vs new */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* OLD */}
          <div className="relative border border-white/10 p-8 md:p-10 bg-white/[0.015]" data-testid="paradigm-old">
            <div className="flex items-center justify-between mb-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF7B5B]">
                // The Old Way
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Manual &amp; Broken
              </div>
            </div>

            <OldVisual />

            <ul className="mt-8 space-y-3">
              {OLD.map((item, i) => (
                <motion.li
                  key={item.t}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3 border-t border-dashed border-white/10 pt-3 first:border-0 first:pt-0"
                >
                  <X size={14} weight="bold" className="text-[#FF7B5B] mt-1 shrink-0" />
                  <div>
                    <div className="font-display font-bold uppercase tracking-tighter text-white text-sm md:text-base">
                      {item.t}
                    </div>
                    <div className="font-body text-[13px] text-zinc-500 leading-relaxed mt-0.5">
                      {item.d}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* NEW */}
          <div
            className="relative border p-8 md:p-10 bg-white/[0.02]"
            style={{ borderColor: "#CCFF0033", boxShadow: "inset 0 1px 0 0 rgba(204,255,0,0.15)" }}
            data-testid="paradigm-new"
          >
            <div className="absolute -top-2 -left-2 w-3 h-3 border-l border-t border-[#CCFF00]" />
            <div className="absolute -top-2 -right-2 w-3 h-3 border-r border-t border-[#CCFF00]" />
            <div className="absolute -bottom-2 -left-2 w-3 h-3 border-l border-b border-[#CCFF00]" />
            <div className="absolute -bottom-2 -right-2 w-3 h-3 border-r border-b border-[#CCFF00]" />

            <div className="flex items-center justify-between mb-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
                // The SVGL Architecture
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Automated &amp; Compounding
              </div>
            </div>

            <NewVisual />

            <ul className="mt-8 space-y-3">
              {NEW.map((item, i) => (
                <motion.li
                  key={item.t}
                  initial={{ opacity: 0, x: 8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3 border-t border-dashed border-white/10 pt-3 first:border-0 first:pt-0"
                >
                  <Check size={14} weight="bold" className="text-[#CCFF00] mt-1 shrink-0" />
                  <div>
                    <div className="font-display font-bold uppercase tracking-tighter text-white text-sm md:text-base">
                      {item.t}
                    </div>
                    <div className="font-body text-[13px] text-zinc-400 leading-relaxed mt-0.5">
                      {item.d}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── The Old Way: chaotic tangle of tools ─────────────────────── */
function OldVisual() {
  const tools = [
    { x: 10, y: 30, w: 92, label: "SPREADSHEET" },
    { x: 140, y: 64, w: 62, label: "CRM" },
    { x: 232, y: 20, w: 76, label: "LIST TOOL" },
    { x: 328, y: 10, w: 88, label: "OUTREACH" },
    { x: 18, y: 108, w: 72, label: "SLACK" },
    { x: 158, y: 120, w: 78, label: "REPORTS" },
    { x: 258, y: 112, w: 88, label: "ENRICHMENT" },
    { x: 362, y: 92, w: 72, label: "MA TOOL" },
  ];
  const links = [
    [102, 42, 140, 76], [202, 50, 232, 32], [308, 32, 328, 22],
    [248, 128, 170, 92], [90, 120, 140, 84], [200, 76, 260, 120],
    [90, 120, 232, 132], [170, 80, 260, 24], [340, 104, 362, 104],
    [236, 130, 328, 104],
  ];
  return (
    <svg viewBox="-4 0 446 170" className="w-full h-auto" overflow="visible">
      {links.map((l, i) => (
        <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]}
          stroke="#FF7B5B44" strokeWidth="0.8" strokeDasharray="2 2" />
      ))}
      {tools.map((t) => (
        <g key={t.label}>
          <rect x={t.x} y={t.y} width={t.w} height="24" fill="#0a0a0a"
            stroke="#3a3a3a" strokeWidth="1" />
          <text x={t.x + t.w / 2} y={t.y + 15}
            textAnchor="middle"
            fill="#909090"
            fontFamily="IBM Plex Mono"
            fontSize="7.5"
            letterSpacing="1.5">
            {t.label}
          </text>
        </g>
      ))}
      <text x="219" y="160" textAnchor="middle" fill="#FF7B5B99"
        fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="2">
        UNCOORDINATED · NO SOURCE OF TRUTH
      </text>
    </svg>
  );
}

/* ── The New Way: signal → engine → 3 outcomes ─────────────── */
function NewVisual() {
  const outputs = [
    { y: 22, label: "CRM UPDATED",  color: "#00F0FF" },
    { y: 82, label: "REP BRIEFED",  color: "#FFB800" },
    { y: 142, label: "SEQUENCE ON", color: "#D946EF" },
  ];
  return (
    <svg viewBox="0 0 440 180" className="w-full h-auto" overflow="visible">
      {/* Signal */}
      <rect x="16" y="66" width="80" height="28" fill="#0a0a0a" stroke="#CCFF00" strokeWidth="1" />
      <text x="56" y="84" textAnchor="middle" fill="#CCFF00" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="2">
        SIGNAL
      </text>

      {/* Line in */}
      <line x1="96" y1="80" x2="180" y2="80" stroke="#CCFF0055" strokeWidth="1" />
      <circle r="2.5" fill="#CCFF00" style={{ filter: "drop-shadow(0 0 4px #CCFF00)" }}>
        <animate attributeName="cx" from="96" to="180" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="cy" values="80" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="1.8s" repeatCount="indefinite" />
      </circle>

      {/* Engine */}
      <rect x="180" y="48" width="88" height="64" fill="#0a0a0a" stroke="#CCFF00" strokeWidth="1.2" />
      <circle cx="224" cy="80" r="12" stroke="#CCFF0088" strokeWidth="1" fill="none">
        <animate attributeName="r" values="10;14;10" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <circle cx="224" cy="80" r="4" fill="#CCFF00" />
      <text x="224" y="128" textAnchor="middle" fill="#CCFF00" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="2.5">
        SVGL ENGINE
      </text>

      {/* Paths out */}
      {outputs.map((o) => {
        const pathD = `M 268 80 C 316 80, 332 ${o.y + 10}, 362 ${o.y + 10}`;
        return (
          <g key={o.label}>
            <path d={pathD} fill="none" stroke={o.color + "77"} strokeWidth="1" />
            <circle r="2.5" fill={o.color} style={{ filter: `drop-shadow(0 0 4px ${o.color})` }}>
              <animateMotion dur="1.8s" repeatCount="indefinite" path={pathD} />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="1.8s" repeatCount="indefinite" />
            </circle>

            <rect x="360" y={o.y} width="3" height="20" fill={o.color} />
            <text x="368" y={o.y + 14} fill={o.color} fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="1.5">
              {o.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
